import React from 'react'
import { DelayedTextInput } from '../../../components/form/textInput'
import { User } from '../../../providers/context'
import { ConferenceLocation } from '../components/conferenceComponents'
import { LocalTable, VisibleColumnDef } from '../../../components/table'
import { makeRequest, showAxiosErrorAsToast, useRequest } from '../../../request'
import { Popups } from '../../../providers/popups'
import { IconEdit } from '../../../icons/library'
import { Loader } from '../../../components/simple/loader'
import { Button, ButtonStyle } from '../../../components/simple/button'
import { Tooltip } from '../../../components/simple/tooltip'
import { Toasts } from '../../../providers/toast'

type TeacherInfo = {
  id: number
  name: string
  location: ConferenceLocation
  studentsInClass: number
  studentsInClassIncluded: number
  invitesSent: number
  appointmentsScheduled: number
}

export const TeacherInfoTab: React.FC<{
  conferenceID: number
  user: User
}> = ({ conferenceID, user }) => {
  const [search, setSearch] = React.useState('')

  const { data, reload } = useRequest<{
    teachers: TeacherInfo[]
    virtualOrInPerson: 'inperson' | 'virtual'
    adminLed: boolean
  }>(
    '/frontend_api/getConferenceTeacherInfoTable',
    React.useMemo(
      () => ({
        conference_id: conferenceID,
      }),
      [conferenceID]
    )
  )

  const virtualOrInPerson = data?.virtualOrInPerson
  const adminLed = data?.adminLed ?? false

  const tableColumns = React.useMemo<VisibleColumnDef<TeacherInfo>[]>(
    () => [
      {
        header: 'Name',
        accessorKey: 'name',
      },
      {
        id: 'room',
        show: virtualOrInPerson !== 'virtual',
        header: 'Room Number',
        accessorFn: (row) => 'Room ' + row.location.roomNumber,
        cell: ({ row }) => (
          <ConferenceLocation
            icon={<IconEdit className="cursor-pointer" />}
            className="!max-w-[300px] !text-wrap"
            location={row.original.location}
            virtualOrInPerson="inperson"
            onClick={() => {
              Popups.prompt({
                title: `Edit Room Number for ${row.original.name}`,
                defaultValue: row.original.location.roomNumber,
                confirm: 'Save',
                cancel: 'Cancel',
              }).then((room) => {
                if (room)
                  makeRequest('/ptc/adminSaveRoom', {
                    teacher_id: row.original.id,
                    room,
                  })
                    .then(reload)
                    .catch(showAxiosErrorAsToast)
              })
            }}
          />
        ),
      },
      {
        id: 'virtualLocation',
        show: virtualOrInPerson !== 'inperson',
        header: 'Virtual Location',
        accessorKey: 'location.virtualLocation',
        cell: ({ row }) => (
          <ConferenceLocation
            icon={<IconEdit className="cursor-pointer" />}
            className="!max-w-[300px] !text-wrap"
            location={row.original.location}
            virtualOrInPerson="virtual"
            onClick={() => {
              Popups.prompt({
                title: `Edit Virtual Location for ${row.original.name}`,
                defaultValue: row.original.location.url,
                confirm: 'Save',
                cancel: 'Cancel',
              }).then((virtual) => {
                if (virtual)
                  makeRequest('/ptc/adminSaveVirtual', {
                    teacher_id: row.original.id,
                    virtual,
                  })
                    .then(reload)
                    .catch(showAxiosErrorAsToast)
              })
            }}
          />
        ),
      },
      {
        header: 'All Students',
        accessorKey: 'studentsInClass',
      },
      {
        id: 'Included Students',
        show: adminLed,
        header: () => (
          <Tooltip text="How many students are in this teachers classes, ignoring excluded classes.">
            <div>Included Students</div>
          </Tooltip>
        ),
        accessorKey: 'studentsInClassIncluded',
      },
      {
        header: 'Invites Sent',
        accessorKey: 'invitesSent',
      },
      {
        header: 'Appointments Scheduled',
        accessorKey: 'appointmentsScheduled',
      },
    ],
    [conferenceID, virtualOrInPerson, adminLed, reload]
  )

  return (
    <div className="mt-4 flex min-h-0 flex-col gap-2">
      <div className="flex items-center gap-2">
        {adminLed && (
          <Button
            buttonStyle={ButtonStyle.warning}
            onClick={async () => {
              if (
                await Popups.simpleConfirm(
                  'Are you sure you want to send instructions to all teachers about how to set their locations?'
                )
              ) {
                makeRequest('/ptc/emailTeachersMissingInfo/' + conferenceID, {}, 'GET')
                  .catch(showAxiosErrorAsToast)
                  .then(() => Toasts.showToast({ message: 'Instructions sent!' }))
              }
            }}
          >
            Send Location Instructions To All Teachers
          </Button>
        )}
        <DelayedTextInput placeholder="Search" value={search} onChange={setSearch} className="ml-auto rounded-full" />
      </div>
      {data ? (
        <LocalTable
          columns={tableColumns}
          data={data.teachers}
          sortable
          pagination={8}
          search={search}
          exportName="Teacher Info"
        />
      ) : (
        <Loader />
      )}
    </div>
  )
}
